// PDF content templates for each guide
// Returns HTML string for the guide content

const pdfStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Inter', Arial, sans-serif;
    line-height: 1.6;
    color: #1f2937;
    padding: 40px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .header {
    text-align: center;
    padding-bottom: 30px;
    border-bottom: 3px solid #D97757;
    margin-bottom: 30px;
  }
  
  .header h1 {
    color: #0A192F;
    font-size: 28px;
    margin-bottom: 10px;
  }
  
  .header .subtitle {
    color: #6b7280;
    font-size: 16px;
  }
  
  .header .brand {
    color: #D97757;
    font-size: 14px;
    margin-top: 15px;
  }
  
  .section {
    margin-bottom: 30px;
    page-break-inside: avoid;
  }
  
  .section h2 {
    color: #0A192F;
    font-size: 18px;
    padding: 12px 15px;
    background: #f3f4f6;
    border-left: 4px solid #D97757;
    margin-bottom: 15px;
  }
  
  .section ul {
    list-style: none;
    padding-left: 0;
  }
  
  .section li {
    padding: 10px 0 10px 30px;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
  }
  
  .section li:before {
    content: "☐";
    position: absolute;
    left: 5px;
    color: #D97757;
    font-size: 16px;
  }
  
  .pro-tips {
    background: #0A192F;
    color: white;
    padding: 25px;
    border-radius: 8px;
    margin-top: 30px;
  }
  
  .pro-tips h2 {
    color: #D97757;
    border: none;
    background: none;
    padding: 0;
    margin-bottom: 15px;
  }
  
  .pro-tips li {
    border-bottom-color: rgba(255,255,255,0.1);
    color: #e5e7eb;
  }
  
  .pro-tips li:before {
    content: "💡";
  }
  
  .footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 2px solid #e5e7eb;
    text-align: center;
    color: #6b7280;
    font-size: 12px;
  }
  
  .footer a {
    color: #D97757;
    text-decoration: none;
  }
  
  .cta-box {
    background: linear-gradient(135deg, #D97757 0%, #c66a4a 100%);
    color: white;
    padding: 25px;
    border-radius: 8px;
    text-align: center;
    margin: 30px 0;
  }
  
  .cta-box h3 {
    margin-bottom: 10px;
  }
  
  @media print {
    body { padding: 20px; }
    .section { page-break-inside: avoid; }
  }
`;

const stormDamageContent = `
  <div class="section">
    <h2>📋 Exterior Roof Inspection</h2>
    <ul>
      <li>Check for missing, cracked, or curled shingles from ground level</li>
      <li>Look for exposed underlayment or bare spots on the roof</li>
      <li>Inspect metal flashing around chimneys and vents for dents</li>
      <li>Check ridge caps and hip shingles for damage</li>
      <li>Document any debris accumulation on the roof</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🏠 Gutter System Check</h2>
    <ul>
      <li>Examine gutters for dents, cracks, or separation</li>
      <li>Check downspouts for damage or displacement</li>
      <li>Look for excessive shingle granules in gutters (sign of hail damage)</li>
      <li>Verify gutters are still properly attached to fascia</li>
      <li>Ensure drainage is directed away from foundation</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🧱 Siding & Exterior Walls</h2>
    <ul>
      <li>Inspect vinyl/aluminum siding for cracks, dents, or holes</li>
      <li>Check wood siding for splintering or punctures</li>
      <li>Look for paint chipping or water staining</li>
      <li>Examine window frames and trim for damage</li>
      <li>Check outdoor fixtures (lights, outlets) for water intrusion</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🪟 Window & Door Assessment</h2>
    <ul>
      <li>Check all windows for cracks, chips, or broken seals</li>
      <li>Inspect window screens for tears or damage</li>
      <li>Test windows and doors for proper operation</li>
      <li>Look for water staining around frames</li>
      <li>Check weatherstripping integrity</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🏚️ Interior Damage Signs</h2>
    <ul>
      <li>Look for new water stains on ceilings and walls</li>
      <li>Check attic for daylight showing through roof</li>
      <li>Inspect around skylights for moisture</li>
      <li>Check for musty odors indicating hidden water damage</li>
      <li>Look for peeling paint or bubbling drywall</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📸 Documentation Steps</h2>
    <ul>
      <li>Photograph all damage with timestamps</li>
      <li>Take wide shots and close-ups of each damaged area</li>
      <li>Save weather reports from the storm date</li>
      <li>Keep receipts for any emergency repairs</li>
      <li>Note the date and time of damage discovery</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📋 Insurance Claim Preparation</h2>
    <ul>
      <li>Locate your insurance policy and review coverage</li>
      <li>Find your insurance company's claims phone number</li>
      <li>Gather your policy number and contact information</li>
      <li>Prepare a detailed list of damages observed</li>
      <li>Note any immediate repairs needed for safety</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>❄️ Minnesota-Specific Considerations</h2>
    <ul>
      <li>Be aware of ice dam potential after winter storms</li>
      <li>Check for foundation cracks from freeze-thaw cycles</li>
      <li>Inspect for wind damage common in open prairie areas</li>
      <li>Know that hail season runs April through September</li>
      <li>Understand most claims must be filed within 60 days</li>
    </ul>
  </div>
  
  <div class="pro-tips">
    <h2>💡 Pro Tips from Minnesota Experts</h2>
    <ul>
      <li>Never climb on your roof after a storm - leave that to professionals</li>
      <li>Document damage before making any temporary repairs</li>
      <li>Get at least three estimates from licensed Minnesota contractors</li>
      <li>Request a copy of the adjuster's report from your insurance company</li>
      <li>Keep all communication with your insurance company in writing</li>
    </ul>
  </div>
`;

const insuranceClaimContent = `
  <div class="section">
    <h2>📋 Before You File: Preparation Checklist</h2>
    <ul>
      <li>Locate your homeowner's insurance policy documents</li>
      <li>Note your policy number and claims phone number</li>
      <li>Review your deductible amount and coverage limits</li>
      <li>Check if you have Replacement Cost Value (RCV) or Actual Cash Value (ACV) coverage</li>
      <li>Understand your policy's time limits for filing claims</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📸 Documentation Essentials</h2>
    <ul>
      <li>Take photos of ALL damage from multiple angles</li>
      <li>Include wide shots showing context and close-ups of specific damage</li>
      <li>Photograph your entire property, not just obvious damage areas</li>
      <li>Save local weather reports and storm data from the event date</li>
      <li>Create a written inventory of damaged items with estimated values</li>
      <li>Keep damaged materials until the adjuster has inspected them</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📞 Filing Your Claim: Step-by-Step</h2>
    <ul>
      <li>Call your insurance company's claims line (not your agent)</li>
      <li>Request a claim number and write it down immediately</li>
      <li>Ask about your policy's specific coverage for storm damage</li>
      <li>Request the timeline for adjuster visit and claim processing</li>
      <li>Get the name and contact info of your assigned claims handler</li>
      <li>Ask about coverage for temporary repairs and additional living expenses</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🔍 Working with the Insurance Adjuster</h2>
    <ul>
      <li>Be present during the adjuster's inspection if possible</li>
      <li>Walk them through all damage areas you've documented</li>
      <li>Point out damage that might not be immediately visible</li>
      <li>Ask questions about anything you don't understand</li>
      <li>Request a copy of the adjuster's full inspection report</li>
      <li>Take notes during the inspection visit</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>💰 Understanding Your Claim Payout</h2>
    <ul>
      <li>Review the scope of work in the insurance estimate carefully</li>
      <li>Understand the difference between ACV and RCV payments</li>
      <li>Know that depreciation may be recoverable after repairs are complete</li>
      <li>Check if the estimate includes proper Minnesota code upgrades</li>
      <li>Verify all damaged areas are included in the estimate</li>
      <li>Understand payment schedules (initial vs. final payment)</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>⚠️ Red Flags to Watch For</h2>
    <ul>
      <li>Pressure to accept a quick settlement</li>
      <li>Claims that damage is "too old" without proper investigation</li>
      <li>Lowball estimates that don't cover full repair costs</li>
      <li>Refusal to provide written documentation</li>
      <li>Adjuster spending very little time on inspection</li>
      <li>Being told you must use a specific contractor</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📝 If You Disagree with the Estimate</h2>
    <ul>
      <li>Request a re-inspection with a different adjuster</li>
      <li>Get independent contractor estimates for comparison</li>
      <li>Document specific items missing from the insurance estimate</li>
      <li>Submit a formal supplement request with supporting evidence</li>
      <li>Consider hiring a public adjuster for complex claims</li>
      <li>Know your right to invoke the appraisal clause</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>⏰ Important Minnesota Timelines</h2>
    <ul>
      <li>File your claim as soon as possible after damage occurs</li>
      <li>Most policies require claims within 60 days of discovery</li>
      <li>Keep records of all communication dates and details</li>
      <li>Note deadlines for supplements and appeals</li>
      <li>Minnesota law requires prompt claim handling by insurers</li>
    </ul>
  </div>
  
  <div class="pro-tips">
    <h2>💡 Minnesota Insurance Claim Pro Tips</h2>
    <ul>
      <li>Your contractor should work WITH your insurance, not against them</li>
      <li>Don't sign any "Assignment of Benefits" without understanding it fully</li>
      <li>Get everything in writing - verbal promises mean nothing</li>
      <li>You have the right to choose your own contractor</li>
      <li>Consider getting a professional inspection before accepting final payment</li>
    </ul>
  </div>
`;

const roofInspectionContent = `
  <div class="section">
    <h2>🔭 Ground-Level Inspection</h2>
    <ul>
      <li>Walk around your entire home and look at the roof from all angles</li>
      <li>Use binoculars to see details without climbing</li>
      <li>Look for obvious missing, curled, or damaged shingles</li>
      <li>Check for sagging areas or uneven roof lines</li>
      <li>Note any debris accumulation or moss/algae growth</li>
      <li>Look for damaged or missing flashing around chimneys and vents</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🏠 Shingle Condition Assessment</h2>
    <ul>
      <li>Check for granule loss (look in gutters for accumulation)</li>
      <li>Look for cracked, curled, or buckled shingles</li>
      <li>Identify any blistering or bubbling on shingle surfaces</li>
      <li>Note shingles that appear darker or lighter than surrounding area</li>
      <li>Check for exposed nail heads or popped fasteners</li>
      <li>Look for signs of previous patch repairs</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🔧 Flashing & Penetrations</h2>
    <ul>
      <li>Inspect chimney flashing for gaps, rust, or separation</li>
      <li>Check plumbing vent boots for cracks or deterioration</li>
      <li>Examine skylight seals and surrounding shingles</li>
      <li>Look at roof-to-wall transitions for proper step flashing</li>
      <li>Check satellite dish and antenna mounting points</li>
      <li>Inspect HVAC penetrations and their seals</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>💧 Gutter & Drainage System</h2>
    <ul>
      <li>Check gutters for proper attachment and slope</li>
      <li>Look for standing water or debris in gutters</li>
      <li>Inspect downspouts for blockages and secure connections</li>
      <li>Verify water flows away from foundation</li>
      <li>Check for ice dam evidence (staining, damage at eaves)</li>
      <li>Look for gutter guards and their condition</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🏚️ Attic Inspection</h2>
    <ul>
      <li>Look for daylight coming through the roof boards</li>
      <li>Check for water stains on rafters and sheathing</li>
      <li>Feel for moisture or soft spots in insulation</li>
      <li>Look for mold or mildew growth</li>
      <li>Check ventilation - soffit vents should be clear</li>
      <li>Inspect for proper insulation depth and coverage</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>❄️ Minnesota Winter Concerns</h2>
    <ul>
      <li>Look for ice dam damage at roof edges and valleys</li>
      <li>Check for icicle formation points</li>
      <li>Inspect for heat cable installation needs</li>
      <li>Verify adequate attic ventilation and insulation</li>
      <li>Check snow guard condition if installed</li>
      <li>Look for signs of condensation issues</li>
    </ul>
  </div>
  
  <div class="pro-tips">
    <h2>💡 Roof Inspection Pro Tips</h2>
    <ul>
      <li>Inspect after major storms, even if no damage is visible</li>
      <li>Schedule professional inspections every 2-3 years</li>
      <li>Document your findings with dated photos</li>
      <li>Address small issues before they become big problems</li>
      <li>Keep records of all maintenance and repairs</li>
    </ul>
  </div>
`;

const contractorHiringContent = `
  <div class="section">
    <h2>🔍 Finding Qualified Contractors</h2>
    <ul>
      <li>Ask neighbors, friends, and family for referrals</li>
      <li>Check with your insurance company for recommended contractors</li>
      <li>Search Minnesota licensed contractor databases</li>
      <li>Look for established local companies (5+ years in business)</li>
      <li>Check online reviews but verify authenticity</li>
      <li>Avoid door-to-door solicitors after storms</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📋 Verification Checklist</h2>
    <ul>
      <li>Verify Minnesota contractor license (required for work over $15,000)</li>
      <li>Confirm general liability insurance ($1M+ recommended)</li>
      <li>Verify workers' compensation insurance coverage</li>
      <li>Check Better Business Bureau rating and complaints</li>
      <li>Look up any legal actions or judgments</li>
      <li>Confirm physical business address (not just a PO Box)</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>❓ Questions to Ask Every Contractor</h2>
    <ul>
      <li>How long have you been in business in Minnesota?</li>
      <li>Can you provide local references from the past year?</li>
      <li>Who will actually be doing the work on my roof?</li>
      <li>What is your timeline for starting and completing the project?</li>
      <li>How do you handle unexpected problems or additional work?</li>
      <li>What warranty do you offer on labor and materials?</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📝 Understanding the Estimate</h2>
    <ul>
      <li>Get at least 3 written estimates for comparison</li>
      <li>Ensure estimates include the same scope of work</li>
      <li>Look for itemized pricing (materials, labor, disposal)</li>
      <li>Verify the estimate includes permit fees if required</li>
      <li>Check that material brands and quality levels are specified</li>
      <li>Ask what's NOT included in the estimate</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🚩 Red Flags to Avoid</h2>
    <ul>
      <li>Contractors who demand large upfront payments (over 30%)</li>
      <li>Pressure to sign immediately or "special deal expires today"</li>
      <li>No physical address or only a cell phone number</li>
      <li>Unwillingness to provide insurance certificates</li>
      <li>Offers to waive your insurance deductible (illegal in MN)</li>
      <li>Extremely low bids compared to other estimates</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📄 Contract Must-Haves</h2>
    <ul>
      <li>Complete company name, address, phone, and license number</li>
      <li>Detailed scope of work with materials specified</li>
      <li>Total price with payment schedule clearly stated</li>
      <li>Start date and estimated completion date</li>
      <li>Warranty terms for both labor and materials</li>
      <li>Process for handling changes or additional work</li>
      <li>Cleanup and debris disposal responsibilities</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>💳 Payment Best Practices</h2>
    <ul>
      <li>Never pay more than 30% upfront</li>
      <li>Pay by check or credit card (not cash)</li>
      <li>Tie payments to completed milestones</li>
      <li>Withhold final payment until all work passes inspection</li>
      <li>Get lien waivers from contractors and suppliers</li>
      <li>Keep all receipts and payment records</li>
    </ul>
  </div>
  
  <div class="pro-tips">
    <h2>💡 Minnesota Contractor Hiring Pro Tips</h2>
    <ul>
      <li>Local contractors have reputation at stake - they're often more reliable</li>
      <li>Check if they're a member of Minnesota Roofing Contractors Association</li>
      <li>Manufacturer-certified installers often provide better warranties</li>
      <li>The lowest bid isn't always the best value</li>
      <li>Trust your instincts - professionalism matters</li>
    </ul>
  </div>
`;

const minnesotaRoofingContent = `
  <div class="section">
    <h2>❄️ Understanding Minnesota's Climate Challenges</h2>
    <ul>
      <li>Temperature extremes: -30°F winters to 100°F summers</li>
      <li>Freeze-thaw cycles cause material expansion and contraction</li>
      <li>Heavy snow loads (40-60 lbs per square foot in some areas)</li>
      <li>Ice dam formation from heat loss and refreezing</li>
      <li>Severe thunderstorms with large hail (April-September)</li>
      <li>High winds, especially in open prairie regions</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🏠 Best Roofing Materials for Minnesota</h2>
    <ul>
      <li><strong>Asphalt Shingles:</strong> Most popular, cost-effective, Class 4 impact-rated recommended</li>
      <li><strong>Metal Roofing:</strong> Excellent for snow shedding, 50+ year lifespan</li>
      <li><strong>Synthetic Slate/Shake:</strong> Durable, lighter weight than natural materials</li>
      <li><strong>Standing Seam Metal:</strong> Premium option, no exposed fasteners</li>
      <li>Avoid: Standard 3-tab shingles (poor hail resistance)</li>
      <li>Consider: Algae-resistant shingles for humid conditions</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🛡️ Ice Dam Prevention</h2>
    <ul>
      <li>Ensure adequate attic insulation (R-49 or higher recommended)</li>
      <li>Maintain proper attic ventilation (1:150 ratio)</li>
      <li>Install ice and water shield along eaves (6 feet minimum)</li>
      <li>Consider heat cables for problem areas</li>
      <li>Keep gutters clean to allow proper drainage</li>
      <li>Seal all attic air leaks to prevent heat loss</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🌡️ Ventilation Requirements</h2>
    <ul>
      <li>Balance intake (soffit) and exhaust (ridge) vents</li>
      <li>Never mix different exhaust vent types on same roof</li>
      <li>Ensure soffit vents aren't blocked by insulation</li>
      <li>Consider power ventilators for complex roof designs</li>
      <li>Proper ventilation extends roof life significantly</li>
      <li>Reduces moisture buildup and prevents mold</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>💰 Minnesota Roofing Costs (2024-2025)</h2>
    <ul>
      <li><strong>Basic Asphalt:</strong> $4.00-$6.00 per square foot</li>
      <li><strong>Premium Asphalt (Class 4):</strong> $6.00-$9.00 per square foot</li>
      <li><strong>Metal Roofing:</strong> $8.00-$14.00 per square foot</li>
      <li><strong>Average MN roof replacement:</strong> $8,000-$15,000</li>
      <li>Prices vary by region, complexity, and material choice</li>
      <li>Always get multiple quotes and compare scope of work</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📋 Minnesota Building Codes</h2>
    <ul>
      <li>Permits required for full roof replacements</li>
      <li>Ice and water shield required at eaves and valleys</li>
      <li>Maximum two layers of shingles (tear-off often required)</li>
      <li>Proper drip edge installation mandated</li>
      <li>Specific requirements for snow load capacity</li>
      <li>Code compliance required for insurance claims</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📅 Seasonal Maintenance Schedule</h2>
    <ul>
      <li><strong>Spring:</strong> Inspect for winter damage, clean gutters, check flashing</li>
      <li><strong>Summer:</strong> Look for heat damage, trim overhanging branches</li>
      <li><strong>Fall:</strong> Clear debris, clean gutters, final inspection before winter</li>
      <li><strong>Winter:</strong> Monitor for ice dams, remove excess snow if needed</li>
      <li>Schedule professional inspection every 2-3 years</li>
      <li>After any major storm, do a ground-level visual inspection</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>🔧 When to Repair vs. Replace</h2>
    <ul>
      <li><strong>Repair:</strong> Damage limited to small area, roof under 15 years old</li>
      <li><strong>Repair:</strong> Minor leaks, few missing shingles, localized issues</li>
      <li><strong>Replace:</strong> Roof over 20 years old with multiple problems</li>
      <li><strong>Replace:</strong> Extensive storm damage, widespread granule loss</li>
      <li><strong>Replace:</strong> Multiple layers of shingles already installed</li>
      <li>Get professional assessment when in doubt</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>📜 Understanding Warranties</h2>
    <ul>
      <li><strong>Manufacturer Warranty:</strong> Covers material defects (25-50 years typical)</li>
      <li><strong>Workmanship Warranty:</strong> Covers installation errors (1-10 years)</li>
      <li>Registration often required to activate full warranty</li>
      <li>Keep all warranty documents and proof of installation</li>
      <li>Understand what voids the warranty (improper ventilation, etc.)</li>
      <li>Pro-rated vs. non-prorated coverage matters</li>
    </ul>
  </div>
  
  <div class="pro-tips">
    <h2>💡 Minnesota Roofing Pro Tips</h2>
    <ul>
      <li>Spring and fall are ideal for roof replacement in Minnesota</li>
      <li>Class 4 shingles cost more upfront but save on insurance and repairs</li>
      <li>Proper ventilation is as important as the roofing material itself</li>
      <li>Don't wait until a leak to address aging roof issues</li>
      <li>Local contractors understand Minnesota's unique challenges best</li>
    </ul>
  </div>
`;

// Get content by guide ID
export const getGuideContent = (guideId) => {
  const contents = {
    'storm-damage-checklist': stormDamageContent,
    'insurance-claim-guide': insuranceClaimContent,
    'roof-inspection-checklist': roofInspectionContent,
    'contractor-hiring-guide': contractorHiringContent,
    'minnesota-roofing-guide': minnesotaRoofingContent
  };
  return contents[guideId] || '<div class="section"><p>Guide content coming soon.</p></div>';
};

// Generate full PDF HTML
export const generateGuidePDF = (guide, subtitle) => {
  const content = getGuideContent(guide.id);
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${guide.title} - Minnesota Storm Damage Resource</title>
      <style>${pdfStyles}</style>
    </head>
    <body>
      <div class="header">
        <h1>${guide.title}</h1>
        <p class="subtitle">${subtitle}</p>
        <p class="brand">Minnesota Storm Damage Resource Center</p>
      </div>
      
      ${content}
      
      <div class="cta-box">
        <h3>Need Professional Help?</h3>
        <p>Connect with trusted Minnesota roofing professionals at stormcaremn.com</p>
      </div>
      
      <div class="footer">
        <p>© ${new Date().getFullYear()} Minnesota Storm Damage Resource Center</p>
        <p>Visit us at <a href="https://stormcaremn.com">stormcaremn.com</a></p>
        <p style="margin-top: 10px; font-size: 11px;">
          This guide is provided as a general resource. Always consult with licensed professionals for thorough assessments.
        </p>
      </div>
    </body>
    </html>
  `;
};
